import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShoppingBag } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [bannedMsg, setBannedMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setBannedMsg("");
    try {
      const u = await login(email, password);
      if (!u.is_admin && u.email_verified === false) { navigate("/verify"); return; }
      toast.success(`Welcome back, ${u.name.split(" ")[0]}!`);
      navigate(u.is_admin ? "/admin" : "/");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "ACCOUNT_BANNED") {
        setBannedMsg("This account has been permanently closed. If you believe this is a mistake, contact info@sellstuff.ai.");
      } else if (detail === "ACCOUNT_ARCHIVED") {
        setBannedMsg("This account is no longer active. If you believe this is a mistake, contact info@sellstuff.ai.");
      } else {
        toast.error(formatApiError(detail) || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center bg-slate-50 px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-white"><ShoppingBag className="h-6 w-6" /></div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Welcome back</h1>
          <p className="text-sm text-slate-500">Log in to buy, sell and track orders</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          {bannedMsg && (
            <div data-testid="login-banned-message" className="rounded-xl border border-red-300 bg-red-50 p-3 text-sm font-medium text-red-700">
              {bannedMsg}
            </div>
          )}
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 rounded-lg" placeholder="you@email.com" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 rounded-lg" placeholder="••••••••" />
          </div>
          <div className="text-right">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-medium text-primary hover:underline">Forgot password?</Link>
          </div>
          <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-full bg-primary font-semibold hover:bg-primary/90">
            {loading ? "Logging in…" : "Log in"}
          </Button>
        </form>
        <p className="mt-6 text-center text-sm text-slate-500">
          New to SellStuff? <Link to="/register" className="font-semibold text-primary">Create an account</Link>
        </p>
      </div>
    </div>
  );
}
